import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { dedupById } from "@/lib/utils";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusIcon, TrashIcon, ArrowRightIcon, ArrowCounterClockwiseIcon } from "@phosphor-icons/react";
import { useSortableRows, SortableTH } from "@/lib/sortable.jsx";
import { useAuth } from "@/lib/auth";
import { RowActions } from "@/components/RowActions";

import FormattedNumberInput from "@/components/FormattedNumberInput";
import SearchablePicker from "@/components/SearchablePicker";
import { baseArticle } from "@/lib/article";
import { resolveShardWO, loomOrderNo, loomWoNo, loomIdentityConflict } from "@/lib/loomSplit";
import LoomSplitGrid from "./production/LoomSplitGrid";
import { ConsumptionStateBadge } from "./production/WeavingWeightDialog";
const inp = "w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-sm focus:outline-none focus:border-[var(--accent)]";
const selCls = "bg-[var(--bg-0)] border-[var(--border)] text-white h-9";

// Option R′ · Loom create submission identity helpers.
const newSubmissionKey = () =>
  (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`);
const RETRYABLE_409 = new Set(["WO_COMMIT_OUTCOME_UNKNOWN", "WO_LIFECYCLE_CHANGED_DURING_POST"]);
const isRetryableSubmitError = (e) => {
  const r = e?.response;
  if (!r || r.status >= 500) return true;
  return r.status === 409 && RETRYABLE_409.has(r.data?.detail?.code);
};


// P17-UAT-18A-0 · DYEING UI REMOVED from the Loom screen. The approved
// architecture places Dyeing under Production Entry → Finishing & Dyeing →
// Dyeing tab (controlled, Not-Yet-Configured until the reconciliation phase).
// Only the Dyeing STAGES entry + its sub-tab are removed here. The generic
// stage-ledger branch, the Dyeing-capable save/startEdit/reverse code paths
// and all shared helpers are LEFT IN PLACE (dead-but-harmless) so the legacy
// Dyeing implementation survives as forensic/reuse evidence — nothing deleted.
// Loom business logic is untouched.
const STAGES = [
  { key: "Loom",   label: "Loom Production" },
];

export default function ProductionEntry({ mode } = {}) {
  // P17-UAT-18A-1 · `mode="dyeing"` reuses this SAME component (no duplicate
  // engine) to render the preserved Dyeing Entry + Register inside the
  // Finishing & Dyeing shell. Default (no mode) = Loom-only screen (18A-0).
  const isDyeing = mode === "dyeing";
  const [tab, setTab] = useState(isDyeing ? "Dyeing" : "Loom");
  // Feb 07, 2026 · Loom sub-tabs — Single Machine (existing modal flow)
  const [rows, setRows] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [orders, setOrders] = useState([]);
  // Feb 07, 2026 · Unified Order Picker — Loom entries must tie back to a
  // customer order (domestic SO OR export ESO) for 100% traceability.
  // `exportOrders` holds the ESO rows; `customers` powers the "Order · Customer"
  // side-by-side label in the picker so operators pick the right doc without
  // hunting the ESO list.
  const [exportOrders, setExportOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  // Mar 2026 · WO-centric refactor · Production Entry is now driven off
  // approved Work Orders. Each WO carries its source SO/ESO + item +
  // customer snapshots, so picking a WO auto-hydrates every downstream
  // field. The old Order picker still renders on non-Loom stages
  // (Finishing / Dyeing / Packing) which continue to key off SO/ESO.
  const [workOrders, setWorkOrders] = useState([]);
  const [items, setItems] = useState([]);
  const [machines, setMachines] = useState([]);
  const [operators, setOperators] = useState([]);
  // Feb 2026 · Departments master — feeds the From/To pickers on the
  // Material Transfer dialog so operators pick from the same source of
  // truth used everywhere else (Machines master, Employee Master, etc.).
  const [departments, setDepartments] = useState([]);
  // Feb 2026 · Defects master — feeds the Reject Reason dropdown on the
  // Finishing / Dyeing / Packing dialogs. Admins manage the list from
  // Masters → Defects; falls back to the four legacy defaults if the
  // master hasn't been seeded yet.
  const [defects, setDefects] = useState([]);
  const [plans, setPlans] = useState([]);
  const [autoTransfer, setAutoTransfer] = useState(false);
  const [open, setOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);

  const emptyEntry = {
    entry_date: new Date().toISOString().slice(0, 10),
    shift: "A", machine_id: "", operator_id: "",
    // Mar 2026 · WO-centric. `work_order_id` is the primary lookup;
    // `order_id` + `order_kind` are auto-hydrated snapshots from the
    // picked WO so downstream (plans, reports) keep working unchanged.
    work_order_id: "",
    order_id: "", order_kind: "",
    // Read-only display snapshots that render alongside the WO picker.
    linked_source_no: "", linked_customer_name: "", product_code: "",
    item_id: "",
    produced_meter: 0, produced_kgs: 0, rejected_meter: 0, rejected_kgs: 0, rejected_reason: "", remarks: "", stage: "Loom",
    // Loom-specific fields (snapshot at entry time)
    machine_model: "", heads: 0, speed_rpm: 0, picks_per_cm: 0,
    shift1_hours: 0, shift2_hours: 0,
  };
  const [form, setForm] = useState(emptyEntry);
  const submitKeyRef = useRef(null);
  // Feb 2026 · Correction Mode — set when the user clicks the Edit
  // pencil on a history row. Save() branches to PUT when non-empty and
  // resets on dialog close. Role-gated to Administrator / Manager /
  // Supervisor via `canEditEntry` below (backend enforces the same
  // rule — the UI just hides the button for other roles).
  const [editingId, setEditingId] = useState("");
  // Feb 2026 · Snapshot of the row exactly as it was hydrated on Edit
  // click. The "Clear" button in edit mode restores this so operators
  // can undo mid-edit tweaks without closing + reopening the dialog.
  const [originalSnapshot, setOriginalSnapshot] = useState(null);
  const { user } = useAuth();
  const canEditEntry = ["Administrator", "Admin", "Manager", "Supervisor"].includes((user?.role || ""));

  const [matchedPlan, setMatchedPlan] = useState(null);
  // Feb 2026 · Multi-SO Machine Stacking — when the backend allows two
  // production blocks to overlap on the same loom (same base article,
  // different SO / ESO), the operator must explicitly pick which SO
  // they're logging meters against. `stackedPlans` holds the full list of
  // active plans for the selected loom so the picker can render.
  const [stackedPlans, setStackedPlans] = useState([]);
  // Feb 2026 · Split-Head Production Mode. When 2+ active plans on the
  // same loom form a Split-Head Group (overlapping windows, all with
  // heads_used > 0, SUM(heads_used) ≤ machine total heads), we switch
  // the entry dialog into batch mode — one row per shard, common shift
  // hours, per-shard head allocation + calculated meters.
  const [splitMode, setSplitMode] = useState(false);
  const [splitRows, setSplitRows] = useState([]);   // hydrated shard rows
  const [splitCommon, setSplitCommon] = useState({ shift1_hours: 0, shift2_hours: 0 });
  const [splitSaving, setSplitSaving] = useState(false);
  const [machineTypes, setMachineTypes] = useState([]);
  // ── Loom auto-fill: when Machine changes, snapshot Model / Heads / Speed
  // AND pull the currently-allocated Article + Sales Order from Capacity Planning.
  // Match logic: pick the Production Plan for this machine whose date range
  // covers today; else the earliest Planned/Running plan; skip Completed ones.
  const activePlansForMachine = (mid) => {
    const openStatuses = ["Running", "Paused", "Planned"];
    return plans.filter(p => p.machine_id === mid && openStatuses.includes(p.status));
  };
  const pickActivePlan = (mid, entryDate) => planQueue(mid, entryDate).auto;
  // LOOM-PLAN-SEL-01 · Authoritative execution sequence: Running first, then
  // Planned/Paused by planned start → plan no → id. Auto-pick = first eligible
  // (not Paused, not starting after the entry date, quantity remaining).
  // Never by WO number, product code, API order or dropdown position.
  const planQueue = (mid, entryDate) => {
    const d = entryDate || form.entry_date || new Date().toISOString().slice(0, 10);
    const queue = [...activePlansForMachine(mid)].sort((a, b) =>
      ((a.status === "Running" ? 0 : 1) - (b.status === "Running" ? 0 : 1))
      || (a.planned_start || "9999").localeCompare(b.planned_start || "9999")
      || String(a.plan_no || "").localeCompare(String(b.plan_no || ""), undefined, { numeric: true })
      || (a.id || "").localeCompare(b.id || ""));
    const eligible = (p) => p.status !== "Paused" && (!p.planned_start || p.planned_start <= d)
      && (!(+p.planned_qty > 0) || (+p.planned_qty) - (+p.actual_qty || 0) > 0);
    return { queue, auto: queue.find(eligible) || null };
  };
  const queueLabel = (i) => (i === 0 ? "First" : i === 1 ? "Next" : `#${i + 1}`);
  // Plan-authoritative field set (WO via plan.work_order_id → source_wo_no).
  // A null plan clears every plan-derived reference so nothing goes stale.
  const planFields = (plan) => {
    if (!plan) {
      return { item_id: "", order_id: "", work_order_id: "", order_kind: "", product_code: "",
               linked_source_no: "", linked_customer_name: "", picks_per_cm: 0 };
    }
    const it = items.find(x => x.id === plan.item_id) || {};
    const woRes = resolveShardWO(plan, workOrders);
    const wo = woRes.woDoc;
    return {
      item_id: plan.item_id, order_id: plan.order_id, work_order_id: woRes.workOrderId,
      order_kind: (wo?.source_type || plan.source_type || "SO").toUpperCase(),
      product_code: plan.product_code || (wo && (wo.item_code || wo.product_code)) || it.code || "",
      linked_source_no: plan.source_order_no || wo?.source_order_no || "",
      linked_customer_name: plan.customer_name || wo?.customer_name || "",
      picks_per_cm: parseFloat(it.standard_picks_per_cm ?? it.picks_per_cm ?? 0),
      _woRes: woRes,
    };
  };
  // Feb 2026 · Split-Head Group detection.
  // A set of plans forms a Split-Head Group when:
  //   • all plans have heads_used > 0
  //   • their planned windows all overlap the same day (any common date)
  //   • SUM(heads_used) ≤ machine's total heads capacity
  // Returns the group array (sorted by plan id for stable split_group_id
  // hashing) or null when the criteria don't hold — in which case the
  // stacked-plan picker keeps its existing "pick the correct SO" flow.
  const machineTotalHeadsFor = (m) => {
    if (!m || !m.machine_type_id) return 0;
    const mt = machineTypes.find(t => t.id === m.machine_type_id);
    return parseInt(mt?.no_of_heads || 0) || 0;
  };
  const detectSplitHeadGroup = (plans, machine) => {
    if (!plans || plans.length < 2) return null;
    if (plans.some(p => !((+p.heads_used) > 0))) return null;
    // Windows must all overlap at least one common date. Compute the
    // latest start and the earliest finish across the set — if max(start)
    // ≤ min(finish) the intersection is non-empty.
    const starts = plans.map(p => p.planned_start || "").filter(Boolean);
    const finishes = plans.map(p => p.planned_finish || "").filter(Boolean);
    if (starts.length !== plans.length || finishes.length !== plans.length) return null;
    const latestStart = starts.reduce((a, b) => (a > b ? a : b));
    const earliestFinish = finishes.reduce((a, b) => (a < b ? a : b));
    if (latestStart > earliestFinish) return null;
    // LOOM-PLAN-SEL-01 · Windows that only touch on one boundary day with
    // different starts are back-to-back, unless the plans are an explicit
    // head split (each uses fewer than all heads and the sum fits the loom).
    const machineTotal = machineTotalHeadsFor(machine);
    const sumHeads = plans.reduce((s, p) => s + (+p.heads_used || 0), 0);
    if (latestStart === earliestFinish && new Set(starts).size > 1) {
      const explicitSplit = machineTotal > 0 && sumHeads <= machineTotal && plans.every(p => (+p.heads_used) < machineTotal);
      if (!explicitSplit) return null;
    }
    // Head capacity check.
    if (machineTotal > 0 && sumHeads > machineTotal) return null;
    return [...plans].sort((a, b) => (a.id || "").localeCompare(b.id || ""));
  };
  const hydrateSplitRow = (plan, machine) => {
    const it = items.find(x => x.id === plan.item_id) || {};
    // P17-UAT-14A REV-B · Capacity Plan is the WO-allocation authority. Resolve
    // each shard's WO from plan.work_order_id → plan.source_wo_no → (legacy
    // heuristic only when the plan lacks WO identity). Never let item/list-order
    // heuristics override the plan's own WO — that collapsed sibling shards
    // (same SO/ESO, newest-first list, stale header item_id) onto one WO.
    const woRes = resolveShardWO(plan, workOrders);
    const wo = woRes.woDoc;
    const speed_rpm = parseInt(machine?.standard_rpm || 0) || 0;
    const picks_per_cm = parseFloat(it.standard_picks_per_cm ?? it.picks_per_cm ?? 0);
    const planned = +(plan.planned_qty || 0);
    const actual = +(plan.actual_qty || 0);
    return {
      plan_id: plan.id,
      order_id: plan.order_id,
      item_id: plan.item_id,
      product_code: plan.product_code || it.code || (wo && (wo.item_code || wo.product_code)) || "",
      source_order_no: plan.source_order_no || "",
      wo_no: woRes.woNo,
      work_order_id: woRes.workOrderId,
      wo_conflict: woRes.conflict,
      order_kind: (wo?.source_type || plan.source_type || "SO").toUpperCase(),
      linked_source_no: plan.source_order_no || wo?.source_order_no || "",
      linked_customer_name: plan.customer_name || wo?.customer_name || "",
      heads_allocated: parseInt(plan.heads_used || 0) || 0,
      speed_rpm,
      picks_per_cm,
      weight_gm_per_meter: +(it.weight_gm_per_meter || 0),
      pending_qty: Math.max(0, planned - actual),
      shift1_hours: 0,
      shift2_hours: 0,
      override: false,
      actual_meters: undefined,       // undefined → grid uses calculated value
      rejected_meter: 0,
      rejected_kgs: 0,
      rejected_reason: "",
      remarks: "",
    };
  };
  const splitGroupIdFor = (plans) => {
    // Deterministic hash from sorted plan-ids so re-opening the same group
    // yields the same split_group_id — backend uses this for idempotency
    // and sibling lookup. Accepts either raw plan docs (`{id}`) or
    // hydrated shard rows (`{plan_id}`) — the caller from `saveSplitBatch`
    // passes hydrated rows.
    const ids = plans.map(p => p.plan_id || p.id).filter(Boolean).sort();
    if (ids.length === 0) return `SG-EMPTY-${Date.now().toString(16).toUpperCase()}`;
    let h = 0;
    const s = ids.join("|");
    for (let i = 0; i < s.length; i++) { h = ((h << 5) - h) + s.charCodeAt(i); h |= 0; }
    return `SG-${Math.abs(h).toString(16).toUpperCase().padStart(8, "0")}-${String(ids[0]).slice(0, 8)}`;
  };
  const exitSplitMode = () => {
    setSplitMode(false);
    setSplitRows([]);
    setSplitCommon({ shift1_hours: 0, shift2_hours: 0 });
  };
  const [autoPlanId, setAutoPlanId] = useState("");
  const onLoomMachineChange = (mid, entryDate) => {
    const m = machines.find(x => x.id === mid) || {};
    const stacked = planQueue(mid, entryDate).queue;
    setStackedPlans(stacked);
    // Split-Head Group detection FIRST — if the stacked plans form a
    // parallel split-head run, switch to batch mode instead of the
    // legacy single-SO picker.
    const splitGroup = detectSplitHeadGroup(stacked, m);
    if (splitGroup) {
      setSplitMode(true);
      const hydrated = splitGroup.map(p => hydrateSplitRow(p, m));
      setSplitRows(hydrated);
      setMatchedPlan(null);
      setAutoPlanId("");
      setForm(f => ({
        ...f, machine_id: mid, ...(entryDate ? { entry_date: entryDate } : {}),
        machine_model: m.machine_model || "",
        heads: parseInt(m.heads || 0),
        speed_rpm: parseInt(m.standard_rpm || 0),
        // Clear single-entry linkage — split mode drives each shard's WO
        // independently and posts through the split-batch endpoint.
        work_order_id: "", order_id: "", order_kind: "",
        linked_source_no: "", linked_customer_name: "",
        product_code: "", item_id: "", picks_per_cm: 0,
      }));
      toast.success(`Split Head Plan Detected — ${splitGroup.length} plans running simultaneously`);
      const conflicted = hydrated.filter(r => r.wo_conflict);
      if (conflicted.length) {
        toast.warning(`Allocation data warning — ${conflicted.length} shard(s) have a Work Order mismatch (${conflicted.map(r => r.wo_no || "?").join(", ")}). Verify Capacity Plan before saving.`);
      }
      return;
    }
    // Legacy path (single plan OR sequential stacked plans not part of
    // a split-head group).
    exitSplitMode();
    // LOOM-PLAN-SEL-01 · Auto-select the first eligible plan in the
    // authoritative execution sequence (single or back-to-back stacked).
    const plan = pickActivePlan(mid, entryDate);
    const pf = planFields(plan);
    const { _woRes: woRes, ...fields } = pf;
    setMatchedPlan(plan || null);
    setAutoPlanId(plan?.id || "");
    setForm(f => ({
      ...f, machine_id: mid, ...(entryDate ? { entry_date: entryDate } : {}),
      machine_model: m.machine_model || "",
      heads: parseInt(m.heads || 0),
      speed_rpm: parseInt(m.standard_rpm || 0),
      ...fields,
    }));
    if (plan) {
      if (woRes?.conflict) {
        toast.warning(
          `Allocation data warning — Work Order ${woRes.woNo || "?"} ${
            woRes.conflict === "WO_NOT_IN_LIST" ? "not found in the loaded list" : "number mismatch"
          }. Verify the Capacity Plan before saving.`
        );
      }
      toast.success(`${stacked.length > 1 ? `${stacked.length} plans on this loom · auto-selected First · ` : "Pulled from plan · "}${woRes?.woNo || "WO —"} · ${fields.product_code || "—"}`);
    } else if (stacked.length) {
      toast.warning("No eligible plan for this date (paused or not yet started) — pick the plan below");
    } else {
      toast.info("No active plan for this machine — pick Work Order manually");
    }
  };
  // Feb 2026 · Lock flag — when a plan drove the WO auto-fill, the WO
  // SearchablePicker is disabled so operators can't override the
  // plan-driven mapping.
  const woLockedByPlan = !!(matchedPlan && form.work_order_id);
  // Explicit stacked-plan pick (Feb 2026). Called when the operator selects
  // one SO from the Stacked Plan picker. Locks Article + Order to that plan.
  const onStackedPlanPick = (planId) => {
    const plan = stackedPlans.find(p => p.id === planId);
    if (!plan) return;
    const { _woRes: woRes, ...fields } = planFields(plan);
    setMatchedPlan(plan);
    setForm(f => ({ ...f, ...fields }));
    if (woRes?.conflict || !fields.work_order_id) toast.warning(`Work Order for this plan could not be resolved (${woRes?.woNo || "?"}). Verify the Capacity Plan.`);
    else toast.success(`Locked to plan ${plan.plan_no || ""} · ${woRes.woNo} · ${fields.product_code || "—"}`);
  };
  const onEntryDateChange = (v) => {
    if (tab === "Loom" && form.machine_id && !editingId) onLoomMachineChange(form.machine_id, v);
    else setForm(f => ({ ...f, entry_date: v }));
  };
  // When Article (Item) changes, snapshot Picks/Cm.
  const onLoomItemChange = (iid) => {
    const it = items.find(x => x.id === iid) || {};
    setForm(f => ({
      ...f, item_id: iid,
      picks_per_cm: parseFloat(it.standard_picks_per_cm ?? it.picks_per_cm ?? 0),
    }));
  };

  // Production (meters) = Speed × 60 ÷ (Picks/cm × 100) × Heads × Total shift hours
  const loomProduction = useMemo(() => {
    const spd = parseFloat(form.speed_rpm || 0);
    const pcm = parseFloat(form.picks_per_cm || 0);
    const heads = parseFloat(form.heads || 0);
    const hrs = parseFloat(form.shift1_hours || 0) + parseFloat(form.shift2_hours || 0);
    if (!spd || !pcm || !heads || !hrs) return 0;
    const mPerHrPerHead = (spd * 60) / (pcm * 100);
    return Math.round(mPerHrPerHead * heads * hrs * 100) / 100;
  }, [form.speed_rpm, form.picks_per_cm, form.heads, form.shift1_hours, form.shift2_hours]);
  const emptyTransfer = {
    transfer_date: new Date().toISOString().slice(0, 10),
    from_stage: "Loom", to_stage: "Finishing",
    order_id: "", item_id: "", machine_id: "", qty_meters: 0, qty_kgs: 0,
    mode: "Manual", remarks: "",
  };
  const [txForm, setTxForm] = useState(emptyTransfer);

  const load = async () => {
    const [e, t, o, it, m, op, pl, s, dp, df, eo, cu, wo, mt] = await Promise.all([
      api.get("/production-entries/"),
      api.get("/material-transfers/"),
      api.get("/sales-orders/"),
      api.get("/items/"),
      api.get("/machines/"),
      api.get("/operators/"),
      api.get("/production-plans/"),
      api.get("/settings/auto-transfer"),
      api.get("/departments/"),
      api.get("/defects/"),
      api.get("/exports/orders/").catch(() => ({ data: [] })),
      api.get("/customers/").catch(() => ({ data: [] })),
      api.get("/production-orders/").catch(() => ({ data: [] })),
      api.get("/machine-types/").catch(() => ({ data: [] })),
    ]);
    setRows(e.data); setTransfers(t.data); setOrders(dedupById(o.data)); setItems(it.data);
    setMachines(m.data); setOperators(op.data); setPlans(pl.data);
    setAutoTransfer(!!s.data?.enabled);
    setDepartments(dp.data || []);
    setDefects(df.data || []);
    // Exports endpoint sometimes returns {rows: [...]} vs a bare array —
    // handle both shapes so the picker doesn't silently render empty.
    const eoRows = Array.isArray(eo.data) ? eo.data : (eo.data?.rows || []);
    setExportOrders(dedupById(eoRows));
    setCustomers(cu.data || []);
    setWorkOrders(dedupById(wo.data || []));
    setMachineTypes(mt.data || []);
  };
  useEffect(() => { load(); }, []);
  const calcRowMath = (row) => {
    const spd   = parseFloat(row.speed_rpm || 0);
    const pcm   = parseFloat(row.picks_per_cm || 0);
    const heads = parseFloat(row.heads || 0);
    const hrs   = parseFloat(row.shift1_hours || 0) + parseFloat(row.shift2_hours || 0);
    const meters = (spd && pcm && heads && hrs)
      ? Math.round((spd * 60) / (pcm * 100) * heads * hrs * 100) / 100
      : 0;
    const it = items.find(x => x.id === row.item_id);
    const wgm = +(it?.weight_gm_per_meter || 0);
    const kgs = wgm > 0 && meters > 0 ? Math.round((meters * wgm) / 10) / 100 : 0;
    return { meters, kgs };
  };
  // Mar 2026 · Clear button — one-tap reset for the Single Loom dialog.
  const resetSingleEntry = () => {
    setForm({ ...emptyEntry, stage: tab });
    setMatchedPlan(null); setStackedPlans([]);
    exitSplitMode();
    toast.info("Cleared — start fresh");
  };
  // Feb 2026 · Split-Head batch save. Composes the header + per-shard
  // payload from the LoomSplitGrid state and posts to the atomic
  // `/production-entries/split-batch` endpoint. On success every plan
  // reconciles independently via _recompute_plan_actual.
  const saveSplitBatch = async (rowsWithCalc) => {
    if (!rowsWithCalc || rowsWithCalc.length < 2) {
      toast.error("Split batch needs at least 2 shards"); return;
    }
    const mid = form.machine_id;
    if (!mid) { toast.error("Pick a machine first"); return; }
    const machine = machines.find(x => x.id === mid) || {};
    const machineTotal = machineTotalHeadsFor(machine);
    const totalHeads = rowsWithCalc.reduce((s, r) => s + (parseInt(r.heads_allocated) || 0), 0);
    if (machineTotal > 0 && totalHeads > machineTotal) {
      toast.error(`Head allocation ${totalHeads} exceeds machine capacity ${machineTotal}`);
      return;
    }
    const split_group_id = splitGroupIdFor(splitRows);
    const header = {
      entry_date: form.entry_date,
      shift: form.shift,
      machine_id: mid,
      operator_id: form.operator_id,
      machine_model: machine.machine_model || form.machine_model || "",
      speed_rpm: parseInt(machine.standard_rpm || form.speed_rpm || 0),
      shift1_hours: parseFloat(splitCommon.shift1_hours) || 0,
      shift2_hours: parseFloat(splitCommon.shift2_hours) || 0,
      remarks: form.remarks || "",
      split_group_id,
    };
    const splits = rowsWithCalc.map(r => {
      const s1 = r.override ? parseFloat(r.shift1_hours || 0) : parseFloat(splitCommon.shift1_hours || 0);
      const s2 = r.override ? parseFloat(r.shift2_hours || 0) : parseFloat(splitCommon.shift2_hours || 0);
      const meters = r.actual_meters ?? r._meters ?? 0;
      const kgs = r._kgs ?? 0;
      return {
        work_order_id: r.work_order_id,
        order_id: r.order_id,
        order_kind: r.order_kind || "SO",
        item_id: r.item_id,
        product_code: r.product_code || "",
        linked_source_no: r.linked_source_no || "",
        linked_customer_name: r.linked_customer_name || "",
        picks_per_cm: parseFloat(r.picks_per_cm) || 0,
        heads_allocated: parseInt(r.heads_allocated) || 0,
        shift1_hours: s1, shift2_hours: s2,
        produced_meter: parseFloat(meters) || 0,
        produced_kgs: parseFloat(kgs) || 0,
        rejected_meter: parseFloat(r.rejected_meter || 0),
        rejected_kgs: parseFloat(r.rejected_kgs || 0),
        rejected_reason: r.rejected_reason || "",
        remarks: r.remarks || "",
      };
    });
    if (splits.some(s => !s.work_order_id)) {
      toast.error("Every shard needs a linked Work Order — reload the page if any plan is unlinked");
      return;
    }
    setSplitSaving(true);
    try {
      const { data } = await api.post("/production-entries/split-batch", { header, splits });
      toast.success(`Split-head batch saved · ${data.count} entries · group ${data.split_group_id}`);
      setOpen(false); exitSplitMode(); setForm({ ...emptyEntry, stage: tab }); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Split batch save failed");
    }
    setSplitSaving(false);
  };

  const stageRows = useMemo(() => rows.filter(r => (r.stage || "Loom") === tab), [rows, tab]);
  // Feb 2026 · Client-side sortable table. DESC-on-`entry_date` is the
  // baseline (Latest-on-Top rule); operators can flip any header on the
  // fly to sort by product code, machine, produced meters, etc.
  const loomSort   = useSortableRows(stageRows, "entry_date", "desc", { storageKey: "production-entry-loom" });
  const stageSort  = useSortableRows(stageRows, "entry_date", "desc", { storageKey: "production-entry-stage" });

  // Mar 2026 · WO-centric Single-Machine hydration. Picking a Work Order
  // auto-fills the picked entry with product_code, picks_per_cm, and
  // snapshots the linked SO/ESO no + customer name — Machine specs
  // (Model / Heads / Speed) continue to arrive when Machine is picked.
  const onLoomWorkOrderChange = (woId) => {
    if (!woId) {
      setForm(f => ({
        ...f, work_order_id: "", order_id: "", order_kind: "",
        linked_source_no: "", linked_customer_name: "", product_code: "",
        item_id: "", picks_per_cm: 0,
      }));
      return;
    }
    const wo = workOrders.find(w => w.id === woId);
    if (!wo) { setForm(f => ({ ...f, work_order_id: woId })); return; }
    const line = (wo.items || [])[0] || {};
    const iid = wo.item_id || line.item_id || "";
    const it = items.find(x => x.id === iid) || {};
    setForm(f => ({
      ...f,
      work_order_id: woId,
      order_id: wo.source_order_id || "",
      order_kind: (wo.source_type || "SO").toUpperCase(),
      linked_source_no: wo.source_order_no || "",
      linked_customer_name: wo.customer_name || "",
      product_code: wo.item_code || line.product_code || it.code || "",
      item_id: iid,
      picks_per_cm: parseFloat(it.standard_picks_per_cm ?? it.picks_per_cm ?? 0),
    }));
    toast.success(`Locked to ${wo.po_no || "WO"} · ${wo.source_order_no || "—"}`);
  };
  // P17-UAT-18A-2 · Dyeing is WO-FIRST. Selecting a WO auto-resolves the
  // read-only SO/ESO reference + exact WO Product/Item. Changing the WO
  // re-resolves/clears all WO-dependent fields so no stale combination remains.
  const onDyeingWorkOrderChange = (woId) => {
    if (!woId) {
      setForm(f => ({ ...f, work_order_id: "", order_id: "", order_kind: "",
        linked_source_no: "", linked_customer_name: "", product_code: "", item_id: "",
        produced_kgs: 0, produced_meter: 0, rejected_kgs: 0, rejected_meter: 0 }));
      return;
    }
    const wo = workOrders.find(w => w.id === woId);
    if (!wo) { setForm(f => ({ ...f, work_order_id: woId })); return; }
    const line = (wo.items || [])[0] || {};
    const iid = wo.item_id || line.item_id || "";
    const it = items.find(x => x.id === iid) || {};
    setForm(f => ({
      ...f,
      work_order_id: woId,
      order_id: wo.source_order_id || "",
      order_kind: (wo.source_type || "SO").toUpperCase(),
      linked_source_no: wo.source_order_no || "",
      linked_customer_name: wo.customer_name || "",
      product_code: wo.item_code || line.product_code || it.code || "",
      item_id: iid,
      produced_kgs: 0, produced_meter: 0, rejected_kgs: 0, rejected_meter: 0,
    }));
  };
  // Feb 2026 · WO options for the Loom picker · Production-Floor
  // Privacy rewrite. Customer identity is scrubbed from the dropdown
  // per operator brief — only the WO No, full Product Code, Article No
  // and Source SO/ESO No are surfaced (all four remain searchable via
  // SearchablePicker's `code + name + meta` haystack). Restricted to
  // non-cancelled WOs (Drafts are still being planned).
  const workOrderOptions = useMemo(() => {
    return (workOrders || [])
      .filter(w => (w.status || "").toLowerCase() !== "cancelled")
      .map(w => {
        const productCode = w.product_code || w.item_code || "—";
        const articleNo = baseArticle(productCode);
        const sourceKind = (w.source_type || "SO").toUpperCase();
        const sourceNo = w.source_order_no || "—";
        const statusChip = w.status === "Approved" ? "APPROVED" : (w.status || "").toUpperCase();
        return {
          value: w.id,
          code: w.po_no || "—",              // WO Number (main label, accent)
          name: productCode,                  // Full Product Code (replaces Customer)
          meta: `Article ${articleNo} · ${sourceKind} ${sourceNo}${statusChip ? ` · ${statusChip}` : ""}`,
          // Extra hidden field so `renderOption` can lay out article
          // and source in a separate second row (smaller text).
          _article: articleNo,
          _source_label: `${sourceKind} ${sourceNo}`,
        };
      })
      .sort((a, b) => (b.code || "").localeCompare(a.code || ""));
  }, [workOrders]);
  // Feb 2026 · Two-line WO row · WO No + Product Code on the top line,
  // Article No + Source SO/ESO in smaller text below. Wide popover
  // (min 480px, max 720px) so the longest Product Codes render in
  // full; long codes gracefully wrap to a second row via
  // `whitespace-normal break-all` instead of truncating. Kept in one
  // place so the single-machine dialog stays consistent.
  const renderWorkOrderOption = (o) => (
    <div className="flex flex-col w-full min-w-0">
      <div className="flex items-start gap-2 min-w-0 flex-wrap">
        <span className="font-mono text-xs text-[var(--accent)] flex-shrink-0">{o.code}</span>
        <span className="font-mono text-sm text-[var(--accent)] whitespace-normal break-all leading-snug flex-1">{o.name}</span>
      </div>
      <div className="text-[10px] text-zinc-500 font-mono pl-0.5 mt-1 whitespace-normal break-words">
        Article {o._article || "—"} · {o._source_label || "—"}
      </div>
    </div>
  );
  // Cancelled orders drop out (no new production against a dead order).
  const customerName = (id) => customers.find(c => c.id === id)?.name || "";
  const combinedOrders = useMemo(() => {
    const so = (orders || [])
      .filter(o => (o.status || "").toLowerCase() !== "cancelled")
      .map(o => ({
        value: `so:${o.id}`,
        raw_id: o.id,
        kind: "SO",
        code: o.order_no || "—",
        name: o.customer_name || customerName(o.customer_id) || "—",
        meta: (o.status || o.auth_status || "").toUpperCase(),
      }));
    const eso = (exportOrders || [])
      .filter(o => (o.status || "").toLowerCase() !== "cancelled")
      .map(o => ({
        value: `eso:${o.id}`,
        raw_id: o.id,
        kind: "ESO",
        code: o.doc_no || o.order_no || "—",
        name: o.buyer_name || customerName(o.buyer_customer_id) || "—",
        meta: `EXPORT · ${(o.status || o.auth_status || "").toUpperCase()}`,
      }));
    return [...so, ...eso].sort((a, b) => (b.code || "").localeCompare(a.code || ""));
  }, [orders, exportOrders, customers]);

  // Resolve the currently-picked prefixed value (so:<id> / eso:<id>) back
  // to a `{kind, raw_id}` pair for lookups.
  const parseOrderVal = (v) => {
    if (!v) return { kind: "", raw_id: "" };
    if (v.startsWith("so:") || v.startsWith("eso:")) {
      const [kind, raw_id] = v.split(":");
      return { kind: kind.toUpperCase(), raw_id };
    }
    // Legacy value — a bare SO id. Treat as SO for backward compat.
    return { kind: "SO", raw_id: v };
  };

  // Auto-Hydrate · items visible in the Loom product-code picker are
  // filtered to those belonging to the currently-picked order's lines.
  // Falls back to the full item list when no order is picked (which
  // shouldn't happen once the order field is Required — but the
  // fallback keeps the picker usable while the operator lands on it
  // first).
  const filteredLoomItems = useMemo(() => {
    if (!form.order_id) return items;
    const { kind, raw_id } = parseOrderVal(form.order_id);
    const src = kind === "ESO"
      ? (exportOrders || []).find(o => o.id === raw_id)
      : (orders || []).find(o => o.id === raw_id);
    if (!src) return items;
    // SO lines carry `product_code` + `article_no`; ESO lines carry the same.
    // Match items by `code` (product_code) OR `article_no` — either identifier
    // on the line resolves to an item row.
    const codes = new Set();
    for (const li of (src.items || src.lines || [])) {
      const pc = (li.product_code || "").trim();
      const an = (li.article_no || "").trim();
      if (pc) codes.add(pc);
      if (an) codes.add(an);
    }
    if (codes.size === 0) return items;
    return items.filter(i => codes.has((i.code || "").trim()) || codes.has((i.article_no || "").trim()));
  }, [form.order_id, items, orders, exportOrders]);

  const save = async (keepOpen = false) => {
    // Split-Head Mode routes through its dedicated batch endpoint. The
    // grid-level Save button calls `saveSplitBatch` directly; this guard
    // stops accidental clicks on the legacy Save when the grid is up.
    if (splitMode) {
      toast.info("Split Head Mode — use the batch Save button in the grid");
      return;
    }
    try {
      // Feb 07, 2026 · Mandatory order link — every Loom entry MUST be
      // tied to a customer order (domestic SO or export ESO) for
      // 100% traceability. Reject the save before hitting the API.
      // Mar 2026 · WO-centric — reject when work_order_id is missing on
      // Loom stage (product/order hydrate off it).
      if (tab === "Loom" && !form.work_order_id) {
        toast.error("Pick a Work Order — required for traceability.");
        return;
      }
      // P17-UAT-18A-2 · Dyeing is WO-first. Block save unless a Work Order is
      // picked and its Product resolved (backend independently re-validates).
      if (tab === "Dyeing") {
        if (!form.work_order_id) { toast.error("Select a Work Order."); return; }
        if (!form.product_code) { toast.error("Product could not be resolved from the selected Work Order."); return; }
      }
      // P17-UAT-15 REV-C1 · Fail-closed production-identity guard. When the
      // entry is driven by an active Capacity Plan, the Work Order (and, when
      // the plan carries one, the Product) MUST match the plan's authoritative
      // allocation. Never silently remap to a sibling WO/product on the same
      // SO/ESO — BLOCK the save so the mismatch is corrected at source.
      if (tab === "Loom" && matchedPlan && loomIdentityConflict(matchedPlan, form)) {
        toast.error(
          "Production identity mismatch. Work Order/Product does not match the active Capacity Plan allocation for this machine."
        );
        return;
      }
      // For Loom entries, produced_meter is derived from the formula.
      const payload = { ...form, stage: tab };
      if (tab === "Loom") payload.produced_meter = loomProduction;
      if (tab === "Loom" && !editingId) payload.production_plan_id = matchedPlan?.id || "";
      // Legacy non-Loom stages still key off the plain SO/ESO picker
      // (`form.order_id` prefixed with so:/eso:). Strip prefix + stamp
      // `order_kind` so backend + reports keep working. Dyeing (18A-2) is
      // WO-first — its order_id is a clean WO-derived id, so skip the strip
      // (backend re-derives order_id/order_kind/product from the WO).
      if (tab !== "Loom" && tab !== "Dyeing" && form.order_id) {
        const parsed = parseOrderVal(form.order_id);
        payload.order_id = parsed.raw_id;
        payload.order_kind = parsed.kind || "SO";
      }
      // Feb 2026 · Correction Mode · PUT when editing, POST when creating.
      // Backend recalculates produced_meter + produced_kgs server-side and
      // stamps the `Corrected by [User] on [Date] - Previous Qty was [X]`
      // audit trail into `remarks` + `corrections[]` so the ledger has a
      // permanent record of every hand-edit.
      if (editingId) {
        await api.put(`/production-entries/${editingId}`, payload);
        toast.success("Entry corrected · audit trail stamped");
      } else if (tab === "Loom") {
        // Option R′ · one Idempotency-Key per genuine submission; reused only on retry of the same payload.
        const sig = JSON.stringify(payload);
        if (!submitKeyRef.current || submitKeyRef.current.sig !== sig) {
          submitKeyRef.current = { sig, key: newSubmissionKey() };
        }
        let res;
        try {
          res = await api.post("/production-entries/", payload, { headers: { "Idempotency-Key": submitKeyRef.current.key } });
        } catch (e) {
          if (!isRetryableSubmitError(e)) submitKeyRef.current = null;
          throw e;
        }
        submitKeyRef.current = null;
        if (res?.headers?.["idempotent-replay"] === "true") {
          const rev = res.data?.status === "Reversed" ? " (since reversed)" : "";
          toast.info(`Already recorded as ${res.data?.entry_no || "existing entry"}${rev}`);
        } else {
          toast.success(autoTransfer ? `Entry recorded · auto-transferred to next stage` : "Entry recorded");
        }
      } else {
        await api.post("/production-entries/", payload);
        toast.success(autoTransfer ? `Entry recorded · auto-transferred to next stage` : "Entry recorded");
      }
      // Feb 2026 · Save & Next flow — keep the modal open, reset only
      // the per-loom fields (Machine, Hours, Qty), preserve the Date so
      // the operator can rapid-fire the rest of the shift. Focus jumps
      // back to the Machine picker via its data-testid trigger.
      if (keepOpen && !editingId) {
        setForm(f => ({
          ...emptyEntry,
          stage: tab,
          entry_date: f.entry_date,        // keep the shift date
          shift: f.shift,                  // keep shift label so it defaults
        }));
        setMatchedPlan(null); setStackedPlans([]);
        load();
        // Nudge focus back to the Machine SearchablePicker trigger so a
        // barcode scanner or Tab key immediately targets the next loom.
        setTimeout(() => {
          const trigger = document.querySelector('[data-testid="entry-machine-trigger"], [data-testid="entry-machine"]');
          if (trigger && trigger.focus) trigger.focus();
        }, 50);
        return;
      }
      setOpen(false); setEditingId(""); setOriginalSnapshot(null); setForm({ ...emptyEntry, stage: tab }); load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((d && typeof d === "object" ? (d.message || d.code) : d) || "Save failed");
    }
  };
  const saveTransfer = async () => {
    try {
      // Feb 2026 bug fix · The department master carries names like
      // "WEAVING / FINISHING / DYEING / PACKING" (uppercase, verbatim from
      // the shop floor). The backend `MaterialTransfer` model however uses
      // a strict `Literal["Loom","Finishing","Dyeing","Packing"]` (matching
      // the internal stage vocabulary — "Loom" ≡ "Weaving"). Without this
      // normaliser every Save Transfer fails 422 with the exact error the
      // operator screenshotted: "from_stage: Input should be 'Loom', ...".
      const _stage = (s) => {
        const v = String(s || "").trim().toLowerCase();
        if (!v) return s;
        if (v === "weaving" || v === "loom") return "Loom";
        if (v === "finishing") return "Finishing";
        if (v === "dyeing") return "Dyeing";
        if (v === "packing") return "Packing";
        return s; // let backend surface a clear error for unknown depts
      };
      const payload = { ...txForm,
        from_stage: _stage(txForm.from_stage),
        to_stage: _stage(txForm.to_stage) };
      await api.post("/material-transfers/", payload);
      toast.success("Material transfer recorded");
      setTransferOpen(false); setTxForm(emptyTransfer); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };
  const remove = async (id) => {
    // DP-LOOM-PE-INTEGRITY-RECOVERY UAT-01 · frontend must mirror the backend
    // lifecycle contract. Effective Production Entries (entry_no assigned,
    // Reversed, or Recovered) cannot be hard-deleted — the trash icon is
    // already suppressed on those rows. Draft rows (no entry_no) reach here.
    if (!window.confirm("Delete this draft entry?")) return;
    try {
      await api.delete(`/production-entries/${id}`);
      toast.success("Draft entry deleted");
      load();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409) {
        // Backend refused because the row is effective / reversed. Surface
        // the business message and prompt the operator to use Reverse.
        toast.error(detail || "Effective Production Entry cannot be hard-deleted — use Reverse to correct it.");
      } else if (status === 403) {
        toast.error(detail || "You do not have permission to delete this entry");
      } else {
        toast.error(detail || "Delete failed");
      }
    }
  };

  // DP-LOOM-PE-INTEGRITY-RECOVERY UAT-02 · Controlled Reverse flow. Opens a
  // modal that captures a mandatory reason (>=4 chars, matches backend
  // `_pe_reverse` contract) and POSTs to /production-entries/{id}/reverse.
  const [reverseTarget, setReverseTarget] = useState(null);
  const [reverseReason, setReverseReason] = useState("");
  const [reverseSubmitting, setReverseSubmitting] = useState(false);
  const openReverse = (row) => {
    setReverseTarget(row);
    setReverseReason("");
  };
  const closeReverse = () => {
    setReverseTarget(null);
    setReverseReason("");
    setReverseSubmitting(false);
  };
  const submitReverse = async () => {
    if (!reverseTarget || reverseSubmitting) return;
    const trimmed = (reverseReason || "").trim();
    if (trimmed.length < 4) {
      toast.error("Reversal requires a reason of at least 4 characters");
      return;
    }
    setReverseSubmitting(true);
    try {
      const resp = await api.post(`/production-entries/${reverseTarget.id}/reverse`, { reason: trimmed });
      const alreadyReversed = resp?.data?.already_reversed;
      toast.success(alreadyReversed
        ? `${reverseTarget.entry_no || "Entry"} is already reversed`
        : `${reverseTarget.entry_no || "Entry"} reversed successfully`);
      closeReverse();
      load();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 400 || status === 403 || status === 404 || status === 409) {
        toast.error(detail || "Unable to reverse this entry");
      } else {
        toast.error(detail || "Reverse failed");
      }
      setReverseSubmitting(false);
    }
  };
  // Backend contract from routes/production.py::_pe_reverse:
  //   • entry_no present (effective row)
  //   • status !== "Reversed"
  //   • Administrator / Admin / Manager role gate
  // The Owner directive on UAT-02 also excludes Recovered PEs from this UAT
  // (`provenance === "PHASE_1B_RECOVERY"`) to protect the historical rows.
  const canReverseRow = (r) => {
    if (!r) return false;
    if (!(r.entry_no || "").trim()) return false;
    if ((r.status || "").toLowerCase() === "reversed") return false;
    if ((r.provenance || "") === "PHASE_1B_RECOVERY") return false;
    const role = user?.role || "";
    return ["Administrator", "Admin", "Manager"].includes(role);
  };
  const canHardDeleteRow = (r) => {
    // Trash icon may only appear on Draft rows (no entry_no) that are
    // neither Reversed nor Recovered. Mirrors backend guard exactly.
    if (!r) return false;
    if ((r.entry_no || "").trim()) return false;
    if ((r.status || "").toLowerCase() === "reversed") return false;
    if ((r.provenance || "") === "PHASE_1B_RECOVERY") return false;
    const role = user?.role || "";
    return ["Administrator", "Admin", "Manager"].includes(role);
  };
  // Feb 2026 · Correction Mode — hydrate the form from a history row and
  // reopen the same dialog the operator uses to create fresh entries.
  // Non-Loom stages need the `so:{id}` / `eso:{id}` prefix on order_id
  // rebuilt so the SearchablePicker picks up the current selection.
  //
  // Legacy Repair: older entries (PE-26-27-00001, PE-26-27-00002) were
  // saved before the WO-centric refactor and are missing either
  // `work_order_id` or `product_code`. Match the row's article/order
  // against the currently-loaded Work Orders + Items so those old
  // records still open cleanly with the WO picker + Product Code chip
  // populated. Backfilled lookups also stamp `product_code` on the
  // form so a subsequent PUT persists the healed snapshot.
  const startEdit = (r) => {
    const stage = r.stage || "Loom";
    setTab(stage);
    setEditingId(r.id);
    const hydrated = { ...emptyEntry, ...r, stage };

    // Legacy repair · resolve missing work_order_id by matching the row's
    // (source_order_no OR order_id) + article/item_code against the loaded
    // Work Orders collection.
    if (!hydrated.work_order_id) {
      const wantOrder = r.linked_source_no || r.source_order_no || r.order_no || "";
      const wantArticle = (r.product_code || r.item_code || r.article_no || "").toString();
      const woMatch = (workOrders || []).find(w => {
        const sameOrder = wantOrder && (w.source_order_no === wantOrder);
        const sameArticle = wantArticle && ((w.product_code || w.item_code || "") === wantArticle);
        return sameOrder && (sameArticle || !wantArticle);
      }) || (workOrders || []).find(w =>
        wantArticle && ((w.product_code || w.item_code || "") === wantArticle)
      );
      if (woMatch) {
        hydrated.work_order_id = woMatch.id;
        hydrated.order_kind = (woMatch.source_type || "SO").toUpperCase();
        hydrated.order_id = woMatch.source_order_id || hydrated.order_id;
        hydrated.linked_source_no = woMatch.source_order_no || hydrated.linked_source_no || "";
      }
    }

    // Legacy repair · resolve missing product_code by looking up item_id
    // in the Items master, or by pulling from the matched Work Order.
    if (!hydrated.product_code) {
      const it = (items || []).find(x => x.id === hydrated.item_id);
      hydrated.product_code = (it && (it.product_code || it.item_code)) || "";
      if (!hydrated.product_code && hydrated.work_order_id) {
        const w = (workOrders || []).find(x => x.id === hydrated.work_order_id);
        hydrated.product_code = (w && (w.product_code || w.item_code)) || "";
      }
    }

    if (stage !== "Loom" && r.order_id && !String(r.order_id).includes(":")) {
      hydrated.order_id = `${(r.order_kind || "so").toLowerCase()}:${r.order_id}`;
    }
    setForm(hydrated);
    // Snapshot for the "Clear" button — deep-clone via JSON so mid-edit
    // mutations to `form` don't bleed into the reset target.
    setOriginalSnapshot(JSON.parse(JSON.stringify(hydrated)));
    setMatchedPlan(null); setStackedPlans([]);
    setOpen(true);
  };
  const removeTx = async (id) => {
    if (!confirm("Delete this transfer?")) return;
    await api.delete(`/material-transfers/${id}`);
    toast.success("Deleted"); load();
  };
  const toggleAuto = async () => {
    const next = !autoTransfer;
    await api.put("/settings/auto-transfer", { enabled: next });
    setAutoTransfer(next);
    toast.success(`Auto-transfer ${next ? "ENABLED" : "disabled"}`);
  };

  const name = (arr, id, k = "name") => arr.find(x => x.id === id)?.[k] || "—";
  const code = (arr, id) => arr.find(x => x.id === id)?.code || "—";

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1">{isDyeing ? "Production Entry · Finishing & Dyeing" : "Daily Reporting"}</div>
          <h1 className="font-display text-3xl font-black tracking-tight text-white">{isDyeing ? "Dyeing Production" : "Loom Production Entry"}</h1>
          <div className="text-xs text-zinc-500 mt-1">{isDyeing ? "Dyeing production register (default view). Click + Record Dyeing to open the existing Dyeing entry workflow." : "Loom production reporting (Entry + Register). Finishing production is entered on Finishing & Dyeing; Material Transfer has its own menu."}</div>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer" data-testid="auto-transfer-toggle">
            <input type="checkbox" checked={autoTransfer} onChange={toggleAuto} className="accent-[var(--accent)]" />
            Auto-transfer to next stage
            <span className={`text-[10px] font-mono uppercase tracking-widest ml-1 ${autoTransfer ? "text-emerald-400" : "text-zinc-600"}`}>{autoTransfer ? "ON" : "OFF"}</span>
          </label>
          {tab !== "transfers" && tab !== "preparatory" && (
            <button data-testid="new-entry-btn" onClick={() => { setForm({ ...emptyEntry, stage: tab }); setMatchedPlan(null); setStackedPlans([]); setOpen(true); }} className="h-10 px-4 bg-[var(--accent)] hover:bg-blue-600 text-white text-sm font-semibold rounded-sm flex items-center gap-2">
              <PlusIcon size={14} weight="bold" />Record {tab}
            </button>
          )}
          {tab === "transfers" && (
            <button data-testid="new-transfer-btn" onClick={() => setTransferOpen(true)} className="h-10 px-4 bg-[var(--accent)] hover:bg-blue-600 text-white text-sm font-semibold rounded-sm flex items-center gap-2">
              <ArrowRightIcon size={14} weight="bold" />New Transfer
            </button>
          )}
        </div>
      </div>

      {/* Sub-tabs — hidden for the dedicated Loom (single stage) and Dyeing
          (mounted in the Finishing & Dyeing shell) screens. */}
      {!isDyeing && (
      <div className="flex flex-wrap gap-1 border-b border-[var(--border)]/60">
        {[...STAGES].map(t => (
          <button key={t.key} data-testid={`pe-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 text-[11px] font-medium uppercase tracking-widest border-b-2 transition-colors ${tab === t.key ? "border-[var(--accent)] text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
            {t.label}
          </button>
        ))}
      </div>
      )}

      {tab === "Loom" ? (
        <>
                    {/* Feb 07, 2026 · Existing Loom production ledger — visible on
              BOTH sub-tabs so operators always see historical entries. */}
          <div className="tactical-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
                <SortableTH sortKey="entry_no"       sortState={loomSort} onRequest={loomSort.requestSort} className="px-4 py-3 font-medium">Entry No</SortableTH>
                <SortableTH sortKey="entry_date"     sortState={loomSort} onRequest={loomSort.requestSort} className="px-4 py-3 font-medium">Date</SortableTH>
                <th className="px-4 py-3 font-medium text-left">SO / ESO</th>
                <th className="px-4 py-3 font-medium text-left">Work Order</th>
                <SortableTH sortKey="machine_id"     sortState={loomSort} onRequest={loomSort.requestSort} className="px-4 py-3 font-medium">Machine</SortableTH>
                <SortableTH sortKey="machine_model"  sortState={loomSort} onRequest={loomSort.requestSort} className="px-4 py-3 font-medium">Model</SortableTH>
                <SortableTH sortKey="heads_working"  sortState={loomSort} onRequest={loomSort.requestSort} align="right" className="px-4 py-3 font-medium">Heads</SortableTH>
                <SortableTH sortKey="speed_rpm"      sortState={loomSort} onRequest={loomSort.requestSort} align="right" className="px-4 py-3 font-medium">Speed (rpm)</SortableTH>
                <SortableTH sortKey="product_code"   sortState={loomSort} onRequest={loomSort.requestSort} className="px-4 py-3 font-medium">Product Code</SortableTH>
                <SortableTH sortKey="picks_per_cm"   sortState={loomSort} onRequest={loomSort.requestSort} align="right" className="px-4 py-3 font-medium">Picks/cm</SortableTH>
                <SortableTH sortKey="shift1_hours"   sortState={loomSort} onRequest={loomSort.requestSort} align="right" className="px-4 py-3 font-medium">Sh-1 (hrs)</SortableTH>
                <SortableTH sortKey="shift2_hours"   sortState={loomSort} onRequest={loomSort.requestSort} align="right" className="px-4 py-3 font-medium">Sh-2 (hrs)</SortableTH>
                <SortableTH sortKey="produced_meter" sortState={loomSort} onRequest={loomSort.requestSort} align="right" className="px-4 py-3 font-medium">Produced (m)</SortableTH>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loomSort.sorted.map(r => (
                <tr key={r.id} className="border-b border-[var(--border)] hover:bg-[var(--bg-2)]/40" data-testid={`entry-${r.id}`}>
                  <td className="px-4 py-3 font-mono text-[var(--accent)] whitespace-nowrap" data-testid={`entry-no-${r.entry_no || r.id.slice(0,8)}`}>
                    {r.entry_no || "—"}
                    {(r.status || "").toLowerCase() === "reversed" && (
                      <span className="ml-2 text-[9px] font-bold px-1 py-0.5 rounded-sm bg-red-500/15 text-red-300 border border-red-500/40 uppercase tracking-widest align-middle" data-testid={`reversed-badge-${r.id}`}>Reversed</span>
                    )}
                    {(r.provenance || "") === "PHASE_1B_RECOVERY" && (
                      <span className="ml-2 text-[9px] font-bold px-1 py-0.5 rounded-sm bg-amber-500/15 text-amber-300 border border-amber-400/40 uppercase tracking-widest align-middle" title="Historically recovered — Phase 1B" data-testid={`recovered-badge-${r.id}`}>Recovered</span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-white">{r.entry_date}</td>
                  <td className="px-4 py-3 font-mono text-zinc-300 whitespace-nowrap" data-testid={`loom-entry-order-${r.id}`}>{loomOrderNo(r, { orders, exportOrders })}</td>
                  <td className="px-4 py-3 font-mono text-zinc-300 whitespace-nowrap" data-testid={`loom-entry-wo-${r.id}`}>{loomWoNo(r, workOrders)}</td>
                  <td className="px-4 py-3 font-mono text-zinc-300">{code(machines, r.machine_id)}</td>
                  <td className="px-4 py-3 font-mono text-zinc-400">{r.machine_model || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono text-zinc-400">{r.heads || 0}</td>
                  <td className="px-4 py-3 text-right font-mono text-zinc-400">{r.speed_rpm || 0}</td>
                  <td className="px-4 py-3 font-mono text-zinc-300">{
                    // Feb 2026 · Fallback chain — item master first,
                    // then the denormalised snapshot on the entry,
                    // then the linked WO. Dashes only when every
                    // source is empty (which the healing migration
                    // rules out for legacy PEs).
                    code(items, r.item_id)
                      || r.product_code
                      || (r.work_order_id && (workOrders.find(w => w.id === r.work_order_id) || {}).product_code)
                      || "—"
                  }</td>
                  <td className="px-4 py-3 text-right font-mono text-zinc-400">{r.picks_per_cm || 0}</td>
                  <td className="px-4 py-3 text-right font-mono text-zinc-400">{r.shift1_hours || 0}</td>
                  <td className="px-4 py-3 text-right font-mono text-zinc-400">{r.shift2_hours || 0}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-emerald-300">
                    {r.produced_meter}
                    {(r.status || "").toLowerCase() !== "reversed" && (r.provenance || "") !== "PHASE_1B_RECOVERY" && (
                      <ConsumptionStateBadge state={r.weaving_consumption_state} />
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <RowActions
                      testid={`loom-entry-actions-${r.id}`}
                      items={[
                        { label: "Edit", tone: "warn", testid: `edit-loom-entry-${r.id}`, show: canEditEntry && (r.status || "").toLowerCase() !== "reversed" && (r.provenance || "") !== "PHASE_1B_RECOVERY", onClick: () => startEdit(r) },
                        { label: "Reverse", tone: "reverse", testid: `reverse-loom-entry-${r.id}`, show: canReverseRow(r), onClick: () => openReverse(r) },
                        { label: "Delete", tone: "danger", testid: `delete-loom-entry-${r.id}`, show: canHardDeleteRow(r), onClick: () => remove(r.id) },
                      ]}
                    />
                  </td>
                </tr>
              ))}
              {loomSort.sorted.length === 0 && <tr><td colSpan={14} className="text-center py-16 text-zinc-500">No Loom entries yet</td></tr>}
            </tbody>
          </table>
        </div>
        </>
      ) : tab !== "transfers" ? (
        <div className="tactical-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
                <SortableTH sortKey="entry_no"        sortState={stageSort} onRequest={stageSort.requestSort} className="px-5 py-3 font-medium">Entry No</SortableTH>
                <SortableTH sortKey="entry_date"      sortState={stageSort} onRequest={stageSort.requestSort} className="px-5 py-3 font-medium">Date</SortableTH>
                <SortableTH sortKey="shift"           sortState={stageSort} onRequest={stageSort.requestSort} className="px-5 py-3 font-medium">Shift</SortableTH>
                <SortableTH sortKey="machine_id"      sortState={stageSort} onRequest={stageSort.requestSort} className="px-5 py-3 font-medium">Machine</SortableTH>
                <SortableTH sortKey="operator"        sortState={stageSort} onRequest={stageSort.requestSort} className="px-5 py-3 font-medium">Operator</SortableTH>
                <SortableTH sortKey="order_no"        sortState={stageSort} onRequest={stageSort.requestSort} className="px-5 py-3 font-medium">Order</SortableTH>
                <SortableTH sortKey="item_code"       sortState={stageSort} onRequest={stageSort.requestSort} className="px-5 py-3 font-medium">Item</SortableTH>
                <SortableTH sortKey="produced_meter"  sortState={stageSort} onRequest={stageSort.requestSort} align="right" className="px-5 py-3 font-medium">Produced (m)</SortableTH>
                <SortableTH sortKey="rejected_meter"  sortState={stageSort} onRequest={stageSort.requestSort} align="right" className="px-5 py-3 font-medium">Rejected (m)</SortableTH>
                <SortableTH sortKey="rejected_kgs"    sortState={stageSort} onRequest={stageSort.requestSort} align="right" className="px-5 py-3 font-medium">Rejected (kgs)</SortableTH>
                <SortableTH sortKey="reject_reason"   sortState={stageSort} onRequest={stageSort.requestSort} className="px-5 py-3 font-medium">Reject Reason</SortableTH>
                <SortableTH sortKey="remarks"         sortState={stageSort} onRequest={stageSort.requestSort} className="px-5 py-3 font-medium">Remarks</SortableTH>
                <th className="text-right px-5 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {stageSort.sorted.map(r => (
                <tr key={r.id} className="border-b border-[var(--border)] hover:bg-[var(--bg-2)]/40" data-testid={`entry-${r.id}`}>
                  <td className="px-5 py-3 font-mono text-[var(--accent)] whitespace-nowrap" data-testid={`entry-no-${r.entry_no || r.id.slice(0,8)}`}>
                    {r.entry_no || "—"}
                    {(r.status || "").toLowerCase() === "reversed" && (
                      <span className="ml-2 text-[9px] font-bold px-1 py-0.5 rounded-sm bg-red-500/15 text-red-300 border border-red-500/40 uppercase tracking-widest align-middle" data-testid={`reversed-badge-${r.id}`}>Reversed</span>
                    )}
                    {(r.provenance || "") === "PHASE_1B_RECOVERY" && (
                      <span className="ml-2 text-[9px] font-bold px-1 py-0.5 rounded-sm bg-amber-500/15 text-amber-300 border border-amber-400/40 uppercase tracking-widest align-middle" title="Historically recovered — Phase 1B" data-testid={`recovered-badge-${r.id}`}>Recovered</span>
                    )}
                  </td>
                  <td className="px-5 py-3 font-mono text-white">{r.entry_date}</td>
                  <td className="px-5 py-3 font-mono text-zinc-400">{r.shift}</td>
                  <td className="px-5 py-3 font-mono text-zinc-400">{code(machines, r.machine_id)}</td>
                  <td className="px-5 py-3 text-zinc-400">{name(operators, r.operator_id)}</td>
                  <td className="px-5 py-3 font-mono text-zinc-400">{name(orders, r.order_id, "order_no")}</td>
                  <td className="px-5 py-3 font-mono text-zinc-400">{code(items, r.item_id)}</td>
                  <td className="px-5 py-3 text-right font-mono text-white">{r.produced_meter}</td>
                  <td className="px-5 py-3 text-right font-mono text-red-400">{r.rejected_meter}</td>
                  <td className="px-5 py-3 text-right font-mono text-red-400">{r.rejected_kgs || 0}</td>
                  <td className="px-5 py-3">
                    {r.rejected_reason
                      ? <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm bg-red-500/15 text-red-300 border border-red-500/30">{r.rejected_reason}</span>
                      : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="px-5 py-3 text-zinc-500 text-xs">{r.remarks}</td>
                  <td className="px-5 py-3 text-right">
                    <RowActions
                      testid={`stage-entry-actions-${r.id}`}
                      items={[
                        { label: "Edit", tone: "warn", testid: `edit-stage-entry-${r.id}`, show: canEditEntry && (r.status || "").toLowerCase() !== "reversed" && (r.provenance || "") !== "PHASE_1B_RECOVERY", onClick: () => startEdit(r) },
                        { label: "Reverse", tone: "reverse", testid: `reverse-stage-entry-${r.id}`, show: canReverseRow(r), onClick: () => openReverse(r) },
                        { label: "Delete", tone: "danger", testid: `delete-stage-entry-${r.id}`, show: canHardDeleteRow(r), onClick: () => remove(r.id) },
                      ]}
                    />
                  </td>
                </tr>
              ))}
              {stageSort.sorted.length === 0 && <tr><td colSpan={13} className="text-center py-16 text-zinc-500">No {tab} entries yet</td></tr>}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="tactical-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
                <th className="text-left px-5 py-3 font-medium">Date</th>
                <th className="text-left px-5 py-3 font-medium">From → To</th>
                <th className="text-left px-5 py-3 font-medium">Order</th>
                <th className="text-left px-5 py-3 font-medium">Item</th>
                <th className="text-left px-5 py-3 font-medium">Machine</th>
                <th className="text-right px-5 py-3 font-medium">Qty (m)</th>
                <th className="text-left px-5 py-3 font-medium">Mode</th>
                <th className="text-left px-5 py-3 font-medium">Remarks</th>
                <th className="text-right px-5 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {transfers.map(r => (
                <tr key={r.id} className="border-b border-[var(--border)] hover:bg-[var(--bg-2)]/40" data-testid={`transfer-${r.id}`}>
                  <td className="px-5 py-3 font-mono text-white">{r.transfer_date}</td>
                  <td className="px-5 py-3 text-zinc-300"><span className="text-zinc-400">{r.from_stage}</span> <ArrowRightIcon className="inline mx-1 text-[var(--accent)]" size={12} weight="bold" /> <span className="text-white">{r.to_stage}</span></td>
                  <td className="px-5 py-3 font-mono text-zinc-400">{name(orders, r.order_id, "order_no")}</td>
                  <td className="px-5 py-3 font-mono text-zinc-400">{code(items, r.item_id)}</td>
                  <td className="px-5 py-3 font-mono text-zinc-400">{r.machine_id ? code(machines, r.machine_id) : "—"}</td>
                  <td className="px-5 py-3 text-right font-mono text-white">{r.qty_meters}</td>
                  <td className="px-5 py-3"><span className={`text-[10px] px-2 py-0.5 rounded-sm font-mono uppercase ${r.mode === "Auto" ? "bg-emerald-500/20 text-emerald-300" : "bg-blue-500/20 text-blue-300"}`}>{r.mode}</span></td>
                  <td className="px-5 py-3 text-zinc-500 text-xs">{r.remarks}</td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => removeTx(r.id)} className="p-1.5 rounded-sm hover:bg-red-500/10 text-zinc-400 hover:text-red-400"><TrashIcon size={14} /></button>
                  </td>
                </tr>
              ))}
              {transfers.length === 0 && <tr><td colSpan={9} className="text-center py-16 text-zinc-500">No material transfers yet. Enable auto-transfer above or click "New Transfer" to create one manually.</td></tr>}
            </tbody>
          </table>
        </div>
      )}


      {/* DP-LOOM-PE-INTEGRITY-RECOVERY UAT-02 · Reverse confirmation dialog.
          Preserves the row (`status="Reversed"`), records reason + reversal
          audit stamp. Mandatory reason (>=4 chars, mirrors backend contract). */}
      <Dialog open={!!reverseTarget} onOpenChange={(v) => { if (!v) closeReverse(); }}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-lg" description="Reverse a posted Production Entry — original row is preserved for audit.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2" data-testid="reverse-dialog-title">
              <ArrowCounterClockwiseIcon size={18} weight="bold" className="text-red-400" />
              Reverse Production Entry
            </DialogTitle>
          </DialogHeader>
          {reverseTarget && (
            <div className="space-y-4 text-sm">
              <div className="p-3 rounded-sm bg-red-500/5 border border-red-500/30 text-red-200 text-[12px] leading-relaxed">
                This will mark <span className="font-mono font-bold text-white">{reverseTarget.entry_no || "this entry"}</span> as <span className="font-mono uppercase font-bold">Reversed</span>. The row stays visible for audit and the source plan is automatically recomputed. This action cannot be undone from the UI.
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 font-mono text-[12px]">
                <div className="text-zinc-500">Entry No</div>
                <div className="text-white" data-testid="reverse-dialog-entry-no">{reverseTarget.entry_no || "—"}</div>
                <div className="text-zinc-500">Source Doc</div>
                <div className="text-white">{reverseTarget.linked_source_no || name(orders, reverseTarget.order_id, "order_no") || "—"}</div>
                <div className="text-zinc-500">WO</div>
                <div className="text-white">{reverseTarget.work_order_id ? ((workOrders.find(w => w.id === reverseTarget.work_order_id) || {}).po_no || (workOrders.find(w => w.id === reverseTarget.work_order_id) || {}).wo_no || "—") : "—"}</div>
                <div className="text-zinc-500">Product</div>
                <div className="text-white truncate" title={reverseTarget.product_code || code(items, reverseTarget.item_id) || "—"}>{reverseTarget.product_code || code(items, reverseTarget.item_id) || "—"}</div>
                <div className="text-zinc-500">Machine</div>
                <div className="text-white">{code(machines, reverseTarget.machine_id) || "—"}</div>
                <div className="text-zinc-500">Produced Qty</div>
                <div className="text-emerald-300 font-bold">{reverseTarget.produced_meter} m</div>
                <div className="text-zinc-500">Entry Date</div>
                <div className="text-white">{reverseTarget.entry_date}</div>
              </div>
              <div>
                <label className="block text-[10px] font-medium uppercase tracking-widest text-zinc-500 mb-1">Reversal Reason <span className="text-red-400">*</span></label>
                <textarea
                  data-testid="reverse-dialog-reason"
                  className="w-full min-h-[80px] px-3 py-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-sm focus:outline-none focus:border-[var(--accent)]"
                  placeholder="Minimum 4 characters — e.g. Wrong machine allocation / Operator error"
                  value={reverseReason}
                  onChange={(e) => setReverseReason(e.target.value)}
                  disabled={reverseSubmitting}
                />
                <div className="text-[10px] font-mono mt-1 text-zinc-500">
                  {reverseReason.trim().length} / 4 characters minimum
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <button
              type="button"
              data-testid="reverse-dialog-cancel"
              onClick={closeReverse}
              disabled={reverseSubmitting}
              className="h-9 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm font-semibold rounded-sm disabled:opacity-50"
            >Cancel</button>
            <button
              type="button"
              data-testid="reverse-dialog-confirm"
              onClick={submitReverse}
              disabled={reverseSubmitting || reverseReason.trim().length < 4}
              className="h-9 px-4 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold rounded-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowCounterClockwiseIcon size={14} weight="bold" />
              {reverseSubmitting ? "Reversing…" : "Confirm Reverse"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Production Entry dialog */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(""); setOriginalSnapshot(null); } }}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-3xl" description="Production Entry action — shift output, wastage and downtime capture.">
          <DialogHeader><DialogTitle className="font-display">{editingId ? `Edit Entry` : `New ${tab} Entry`}{editingId && <span className="ml-3 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm bg-amber-500/20 text-amber-300 border border-amber-500/30">CORRECTION MODE</span>}</DialogTitle></DialogHeader>

          {tab === "Loom" ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <F label="Date">
                  <input type="date" data-testid="entry-date" value={form.entry_date} onChange={e => onEntryDateChange(e.target.value)} className={inp} />
                </F>
                <F label="Machine No">
                  <SearchablePicker
                    testid="entry-machine"
                    value={form.machine_id}
                    onChange={onLoomMachineChange}
                    placeholder="Select machine"
                    headerHint="Search by loom code (L201) or model"
                    options={machines.map(m => ({ value: m.id, code: m.code, name: m.machine_model || m.machine_type || "", meta: m.status || "" }))}
                  />
                </F>
                <F label={<span>Work Order <span className="text-red-400">*</span></span>}>
                  {splitMode ? (
                    <div data-testid="split-mode-wo-placeholder"
                         className="h-9 px-3 flex items-center bg-purple-500/10 border border-purple-500/40 rounded-sm font-mono text-[11px] text-purple-200 uppercase tracking-widest">
                      Split Mode · {splitRows.length} WOs
                    </div>
                  ) : (
                  <SearchablePicker
                    testid="entry-wo"
                    value={form.work_order_id}
                    onChange={onLoomWorkOrderChange}
                    placeholder="Pick Work Order…"
                    headerHint={woLockedByPlan ? "Locked by active Capacity Plan on this machine — clear machine to override" : "Type WO number or any part of the Product Code"}
                    options={workOrderOptions}
                    renderOption={renderWorkOrderOption}
                    popoverClassName="min-w-[480px] max-w-[720px]"
                    itemClassName="py-2.5"
                    disabled={woLockedByPlan}
                  />
                  )}
                </F>
              </div>

              {/* Feb 2026 · Split-Head Mode banner + grid. Auto-activated
                  when the picked loom has a Split-Head Group of plans
                  (2+ active plans, overlapping windows, heads_used>0,
                  SUM(heads) ≤ machine total). Everything downstream of
                  the header row (WO chip, single plan progress, stacked
                  picker, calc panel, hours) is hidden while split mode
                  is on — the grid owns save via `saveSplitBatch`. */}
              {splitMode && (
                <div className="space-y-3" data-testid="split-mode-container">
                  <div data-testid="split-head-banner"
                       className="rounded-sm border border-purple-500/50 bg-purple-500/10 px-4 py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="inline-block w-2 h-2 rounded-full bg-purple-400 animate-pulse"></span>
                        <div>
                          <div className="text-[11px] uppercase tracking-widest text-purple-300 font-mono font-bold">
                            SPLIT HEAD PRODUCTION — {splitRows.length} Variants — {splitRows.reduce((s,r) => s + (+r.heads_allocated||0), 0)}/{machineTotalHeadsFor(machines.find(x => x.id === form.machine_id))} Heads Allocated
                          </div>
                          <div className="text-[10px] text-zinc-400 mt-0.5">Split Head Plan Detected — {splitRows.length} plans running simultaneously.</div>
                        </div>
                      </div>
                      <button data-testid="split-mode-exit"
                              onClick={() => exitSplitMode()}
                              className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-white px-2 py-1 rounded-sm border border-[var(--border)]">
                        Exit Split Mode
                      </button>
                    </div>
                  </div>

                  {/* P17-UAT-14A REV-B1 · Presentation-only Capacity Plan
                      allocation summary. Reads the SAME hydrated splitRows that
                      feed saveSplitBatch — DISPLAY identity === SAVE identity.
                      No independent WO reconstruction. */}
                  <div data-testid="split-alloc-summary"
                       className="rounded-sm border border-[var(--border)] bg-[var(--bg-0)]/60 px-4 py-3">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2.5">
                      Auto-Pulled From Capacity Plan · Split Head
                    </div>
                    <div className="space-y-2.5">
                      {splitRows.map((r, i) => (
                        <div key={r.plan_id} data-testid={`split-alloc-row-${i}`}
                             className="flex items-start justify-between gap-3 pb-2.5 border-b border-[var(--border)]/60 last:border-0 last:pb-0">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono text-[var(--accent)] text-[11px]">#{i + 1}</span>
                              <span data-testid={`split-alloc-wo-${i}`}
                                    className="font-mono text-white text-[11px] font-semibold">{r.wo_no || "—"}</span>
                              <span data-testid={`split-alloc-eso-${i}`}
                                    className="font-mono text-zinc-400 text-[11px]">{r.source_order_no || "—"}</span>
                              {r.wo_conflict && (
                                <span data-testid={`split-alloc-warn-${i}`}
                                      className="text-[9px] uppercase tracking-widest text-amber-300 border border-amber-400/40 rounded-sm px-1 py-0.5">WO data warning</span>
                              )}
                            </div>
                            <div data-testid={`split-alloc-product-${i}`}
                                 className="font-mono text-zinc-300 text-[11px] mt-1 break-words whitespace-normal">{r.product_code || "—"}</div>
                          </div>
                          <div data-testid={`split-alloc-heads-${i}`}
                               className="font-mono text-emerald-300 text-[11px] whitespace-nowrap shrink-0">{(parseInt(r.heads_allocated) || 0)} Heads</div>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-end mt-2.5 pt-2 border-t border-[var(--border)]">
                      <span data-testid="split-alloc-total-heads"
                            className="text-[10px] uppercase tracking-widest text-zinc-400 font-mono">
                        Total Heads {splitRows.reduce((s, r) => s + (parseInt(r.heads_allocated) || 0), 0)} / {machineTotalHeadsFor(machines.find(x => x.id === form.machine_id))}
                      </span>
                    </div>
                  </div>

                  <LoomSplitGrid
                    splits={splitRows}
                    machine={machines.find(x => x.id === form.machine_id)}
                    machineTotalHeads={machineTotalHeadsFor(machines.find(x => x.id === form.machine_id))}
                    common={splitCommon}
                    onCommonChange={setSplitCommon}
                    onRowChange={(idx, patch) => setSplitRows(rows => rows.map((r, i) => i === idx ? { ...r, ...patch } : r))}
                    onSaveBatch={saveSplitBatch}
                    onCancel={() => { setOpen(false); exitSplitMode(); }}
                    saving={splitSaving}
                  />
                </div>
              )}

              {/* Mar 2026 · WO auto-hydrated read-only chips — Customer
                  removed per operator brief. Product Code widened to
                  2 cols so the full technical string (e.g. RNJ/38/…)
                  fits without wrap. WO/SO No right-aligned per new UI
                  standard; Product Code stays left-aligned mono. */}
              {!splitMode && form.work_order_id && (
                <div className="grid grid-cols-3 gap-3 p-3 rounded-sm bg-emerald-500/5 border border-emerald-500/30" data-testid="loom-wo-hydrated">
                  <div className="col-span-2">
                    <ReadonlyField label="Product Code" value={form.product_code || "—"} testid="loom-wo-product" />
                  </div>
                  <div className="text-right">
                    <div className="overline mb-0.5 text-right">Linked {form.order_kind || "SO"} No</div>
                    <div className="font-mono text-white text-right" data-testid="loom-wo-source-no">{form.linked_source_no || "—"}</div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3" style={{display: "none"}}>
                <F label="Product Code (legacy · hidden)">
                  <SearchablePicker
                    testid="entry-item"
                    value={form.item_id}
                    onChange={onLoomItemChange}
                    placeholder="Hidden"
                    disabled
                    options={[]}
                  />
                </F>
              </div>

              {/* Plan match banner — shows which Capacity Plan the Article/Order were pulled from */}
              {!splitMode && matchedPlan && (
                <div data-testid="loom-plan-match" className="flex items-center justify-between rounded-sm border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-[11px]">
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                    <span className="uppercase tracking-widest text-emerald-300 font-mono">Auto-pulled from Capacity Plan</span>
                    <span className="text-zinc-300">·</span>
                    <span className="text-white font-mono">{orders.find(o => o.id === matchedPlan.order_id)?.order_no || "—"}</span>
                    <span className="text-zinc-500">→</span>
                    <span className="text-white font-mono">{items.find(i => i.id === matchedPlan.item_id)?.code || "—"}</span>
                  </div>
                  <span className="text-zinc-500 font-mono">{matchedPlan.status} · {matchedPlan.planned_start} → {matchedPlan.planned_finish}</span>
                </div>
              )}

              {/* Feb 2026 · Multi-SO Machine Stacking picker — when 2+ active
                  plans share this loom (same base article, different SO / ESO)
                  the operator MUST pick which order they're logging meters
                  against. Prevents cross-order attribution errors. */}
              {!splitMode && (stackedPlans.length > 1 || (stackedPlans.length === 1 && !matchedPlan)) && (
                <div data-testid="stacked-plan-picker" className={`min-w-0 rounded-sm border px-3 py-2.5 space-y-2 ${matchedPlan ? "border-cyan-500/40 bg-cyan-500/5" : "border-amber-500/50 bg-amber-500/10"}`}>
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-widest">
                    <div className="flex items-center gap-2">
                      <span className={`inline-block w-2 h-2 rounded-full ${matchedPlan ? "bg-cyan-400" : "bg-amber-400 animate-pulse"}`}></span>
                      <span className={`font-mono ${matchedPlan ? "text-cyan-300" : "text-amber-300"}`} data-testid="stacked-plan-count">
                        {stackedPlans.length} plan{stackedPlans.length > 1 ? "s" : ""} queued on this loom · back-to-back
                      </span>
                    </div>
                    <span className="text-zinc-500 normal-case tracking-normal" data-testid="stacked-plan-state">
                      {matchedPlan ? (matchedPlan.id === autoPlanId ? "· auto-selected (execution sequence)" : "· selected manually") : "· pick the plan"}
                    </span>
                  </div>
                  <Select value={matchedPlan?.id || ""} onValueChange={onStackedPlanPick}>
                    <SelectTrigger data-testid="entry-stacked-plan" className={`${selCls} h-auto min-h-9 min-w-0 text-left whitespace-normal [&>span]:whitespace-normal [&>span]:break-words [&>span]:line-clamp-2`}>
                      <SelectValue placeholder="Pick the plan you're producing for..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-72 max-w-[min(90vw,720px)]">
                      {stackedPlans.map((p, i) => {
                        const woNo = resolveShardWO(p, workOrders).woNo || p.source_wo_no || "WO ?";
                        const remaining = Math.max(0, (+p.planned_qty || 0) - (+p.actual_qty || 0));
                        return (
                          <SelectItem key={p.id} value={p.id} data-testid={`stacked-plan-opt-${p.id}`}>
                            <span className="font-mono whitespace-normal break-words">
                              <b>{queueLabel(i)}</b> · {woNo} · {p.product_code || "—"} · {p.planned_start || "?"} → {p.planned_finish || "?"} · {p.status} · {remaining.toLocaleString("en-IN")}m left{p.plan_no ? ` · ${p.plan_no}` : ""}{p.id === autoPlanId ? " · AUTO" : ""}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Plan progress indicator — actual vs planned + projected impact of THIS entry.
                  Colour bands: emerald ≥ 75%, amber 40–75%, red < 40% OR overdue.
                  "After This Entry" shows where the plan will land once the operator saves. */}
              {!splitMode && matchedPlan && (() => {
                const planned = +(matchedPlan.planned_qty || 0);
                const actual = +(matchedPlan.actual_qty || 0);
                const pct = planned > 0 ? Math.min(100, (actual / planned) * 100) : 0;
                const projected = actual + (loomProduction || 0);
                const projectedPct = planned > 0 ? Math.min(100, (projected / planned) * 100) : 0;
                const today = new Date();
                const finish = matchedPlan.planned_finish ? new Date(matchedPlan.planned_finish) : null;
                const daysLeft = finish ? Math.ceil((finish - today) / 86400000) : null;
                const overdue = daysLeft !== null && daysLeft < 0 && matchedPlan.status !== "Completed";
                const tone = overdue ? "border-red-500/50 bg-red-500/10 text-red-200"
                  : pct >= 75 ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-100"
                  : pct >= 40 ? "border-amber-500/40 bg-amber-500/10 text-amber-100"
                  : "border-blue-500/30 bg-blue-500/5 text-blue-100";
                const fillTone = overdue ? "bg-red-500/70" : pct >= 75 ? "bg-emerald-500/70" : pct >= 40 ? "bg-amber-500/70" : "bg-blue-500/70";
                return (
                  <div data-testid="loom-plan-progress" className={`rounded-sm border px-3 py-2.5 space-y-2 ${tone}`}>
                    <div className="flex items-center justify-between text-[11px]">
                      <div className="overline">Plan Progress</div>
                      <div className="font-mono text-[10px]">
                        {daysLeft === null ? "—"
                          : overdue ? <span className="font-bold uppercase tracking-widest">⚠ {Math.abs(daysLeft)}d overdue</span>
                          : daysLeft === 0 ? <span className="font-bold uppercase tracking-widest">Due today</span>
                          : <span><span className="text-white font-bold">{daysLeft}</span> day{daysLeft === 1 ? "" : "s"} left</span>}
                      </div>
                    </div>

                    {/* Base progress bar (actual) + projected overlay */}
                    <div className="relative h-2.5 rounded-sm bg-[var(--bg-0)] border border-[var(--border)] overflow-hidden">
                      <div className={`absolute inset-y-0 left-0 ${fillTone} transition-all`} style={{ width: `${pct}%` }}></div>
                      {loomProduction > 0 && projectedPct > pct && (
                        <div className="absolute inset-y-0 bg-white/20 border-l border-white/40" style={{ left: `${pct}%`, width: `${projectedPct - pct}%` }} title={`After this entry (+${loomProduction}m)`}></div>
                      )}
                    </div>

                    <div className="flex items-center justify-between text-[10px] font-mono">
                      <span data-testid="loom-plan-actual"><span className="text-white font-semibold">{actual.toLocaleString()}</span> / {planned.toLocaleString()} m</span>
                      <span data-testid="loom-plan-pct" className="font-semibold">{pct.toFixed(1)}%</span>
                    </div>

                    {loomProduction > 0 && (
                      <div data-testid="loom-plan-projected" className="pt-1.5 border-t border-current/20 flex items-center justify-between text-[10px] font-mono">
                        <span className="text-zinc-400 uppercase tracking-widest">After This Entry</span>
                        <span>
                          <span className="text-white font-semibold">{projected.toLocaleString()}</span> m
                          <span className="mx-1 text-zinc-500">·</span>
                          <span className="font-semibold">{projectedPct.toFixed(1)}%</span>
                          <span className="ml-1 text-emerald-300">+{loomProduction.toLocaleString()}m</span>
                        </span>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Auto-populated snapshot fields (read-only) */}
              {!splitMode && (
              <div className="grid grid-cols-4 gap-3 p-3 rounded-sm bg-[var(--bg-0)]/60 border border-[var(--border)]">
                <ReadonlyField label="Machine Model" value={form.machine_model || "—"} testid="loom-model" />
                <ReadonlyField label="No of Heads" value={form.heads || 0} testid="loom-heads" />
                <ReadonlyField label="Speed (rpm)" value={form.speed_rpm || 0} testid="loom-speed" />
                <ReadonlyField label="Picks / cm" value={form.picks_per_cm || 0} testid="loom-picks" />
              </div>
              )}

              {!splitMode && (
              <div className="grid grid-cols-2 gap-3">
                <F label="Actual 1st Shift Running Hours">
                  <FormattedNumberInput step="0.1" min="0" max="12" data-testid="loom-shift1"
                    value={form.shift1_hours}
                    onChange={e => setForm({ ...form, shift1_hours: parseFloat(e.target.value) || 0 })}
                    className={inp} />
                </F>
                <F label="Actual 2nd Shift Running Hours">
                  <FormattedNumberInput step="0.1" min="0" max="12" data-testid="loom-shift2"
                    value={form.shift2_hours}
                    onChange={e => setForm({ ...form, shift2_hours: parseFloat(e.target.value) || 0 })}
                    className={inp} />
                </F>
              </div>
              )}

              {/* Live calculated production — split into TWO side-by-side
                  tiles: Meters (formula) + Kgs (reverse formula). Feb 2026 ·
                  user asked for a distinct "Total Kgs Produced" tile so
                  supervisors can sanity-check against the beam scale. */}
              {!splitMode && (
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-sm border border-[var(--accent)]/40 bg-[var(--accent)]/5 px-4 py-3 flex items-center justify-between" data-testid="loom-calc-panel">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">Calculated · Meters</div>
                    <div className="text-[10px] text-zinc-600 mt-0.5 font-mono">
                      Speed × 60 ÷ (Picks×100) × Heads × Hours
                    </div>
                  </div>
                  <div className="text-right">
                    <div data-testid="loom-production-value" className="font-display text-3xl font-black text-white">{loomProduction}</div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">meters</div>
                  </div>
                </div>
                {(() => {
                  const it = items.find(x => x.id === form.item_id);
                  const wgm = +(it?.weight_gm_per_meter || 0);
                  const kgs = wgm > 0 && loomProduction > 0 ? (loomProduction * wgm) / 1000 : 0;
                  const hasData = wgm > 0 && loomProduction > 0;
                  return (
                    <div className={`rounded-sm border px-4 py-3 flex items-center justify-between ${hasData ? "border-emerald-500/40 bg-emerald-500/5" : "border-[var(--border)] bg-[var(--bg-2)]/30"}`} data-testid="loom-calc-kgs-panel">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total Kgs Produced</div>
                        <div className="text-[10px] text-zinc-600 mt-0.5 font-mono">
                          {hasData ? `Meters × ${wgm} g/m ÷ 1000` : "Needs item + weight g/m"}
                        </div>
                      </div>
                      <div className="text-right">
                        <div data-testid="loom-calc-kgs" className={`font-display text-3xl font-black ${hasData ? "text-emerald-300" : "text-zinc-600"}`}>{hasData ? kgs.toFixed(2) : "—"}</div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">kgs</div>
                      </div>
                    </div>
                  );
                })()}
              </div>
              )}

              {!splitMode && (
              <div className="grid grid-cols-1 gap-3">
                <F label="Remarks">
                  <input value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} className={inp} />
                </F>
              </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <F label="Date"><input type="date" data-testid="entry-date" value={form.entry_date} onChange={e => onEntryDateChange(e.target.value)} className={inp} /></F>
              <F label="Shift">
                <Select value={form.shift} onValueChange={v => setForm({ ...form, shift: v })}>
                  <SelectTrigger data-testid="entry-shift" className={selCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                    {["A","B","C"].map(s => <SelectItem key={s} value={s}>Shift {s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
              <F label="Machine">
                <Select value={form.machine_id} onValueChange={v => setForm({ ...form, machine_id: v })}>
                  <SelectTrigger data-testid="entry-machine" className={selCls}><SelectValue placeholder="Select machine" /></SelectTrigger>
                  <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                    {machines.map(m => <SelectItem key={m.id} value={m.id}>{m.code}</SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
              <F label="Operator">
                <Select value={form.operator_id} onValueChange={v => setForm({ ...form, operator_id: v })}>
                  <SelectTrigger className={selCls}><SelectValue placeholder="Select operator" /></SelectTrigger>
                  <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                    {operators.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
              <F label={<span>Work Order <span className="text-red-400">*</span></span>}>
                <SearchablePicker
                  testid="dyeing-entry-wo"
                  value={form.work_order_id}
                  onChange={onDyeingWorkOrderChange}
                  placeholder="Pick Work Order…"
                  headerHint="Type WO number or any part of the Product Code"
                  options={workOrderOptions}
                  renderOption={renderWorkOrderOption}
                  popoverClassName="min-w-[480px] max-w-[720px]"
                  itemClassName="py-2.5"
                />
              </F>
              <F label="SO / ESO Reference">
                <div data-testid="dyeing-entry-soeso" className="h-9 px-3 flex items-center bg-[var(--bg-2)] border border-[var(--border)] rounded-sm font-mono text-xs text-zinc-300">
                  {form.linked_source_no || "—"}
                </div>
              </F>
              <F label="Item / Product" className="col-span-2">
                <div data-testid="dyeing-entry-product" className="h-9 px-3 flex items-center bg-[var(--bg-2)] border border-[var(--border)] rounded-sm font-mono text-xs text-zinc-300 truncate">
                  {form.product_code || "—"}
                </div>
              </F>
              {/* Feb 2026 · dual Kgs↔Meters entry — same conversion pattern
                  as Material Transfer. Editing kg auto-fills meters and vice
                  versa using weight_gm_per_meter from Product Master. Either
                  field can still be edited independently to record trim /
                  waste allowances. */}
              <F label="Produced (Kgs)">
                <FormattedNumberInput
                  step="0.001"
                  data-testid="entry-produced-kgs"
                  value={form.produced_kgs || 0}
                  onChange={e => {
                    const kgs = parseFloat(e.target.value) || 0;
                    const it = items.find(x => x.id === form.item_id);
                    const wgm = +(it?.weight_gm_per_meter || 0);
                    const meters = wgm > 0 ? Math.round(((kgs * 1000) / wgm) * 100) / 100 : form.produced_meter;
                    setForm({ ...form, produced_kgs: kgs, produced_meter: wgm > 0 ? meters : form.produced_meter });
                  }}
                  className={inp}
                  placeholder="0.000"
                />
              </F>
              <F label="Produced (meters)"><FormattedNumberInput data-testid="entry-produced" value={form.produced_meter} onChange={e => setForm({ ...form, produced_meter: parseFloat(e.target.value) || 0 })} className={inp} /></F>
              {/* Conversion hint — reads weight_gm_per_meter live so operators
                  can sanity-check the maths and spot missing master data. */}
              {(() => {
                const it = items.find(x => x.id === form.item_id);
                const wgm = +(it?.weight_gm_per_meter || 0);
                return (
                  <div className="col-span-2 -mt-1">
                    {form.item_id ? (
                      wgm > 0 ? (
                        <div data-testid="entry-conversion-hint" className="text-[10px] text-zinc-500 font-mono">
                          Weight per meter: <span className="text-[var(--accent)]">{wgm} g/m</span> · Formula: meters = (kgs × 1000) ÷ {wgm}
                          {(+form.produced_kgs > 0) && <> · <span className="text-emerald-400">{(form.produced_kgs * 1000 / wgm).toFixed(2)} m from {form.produced_kgs} kg</span></>}
                        </div>
                      ) : <div className="text-[10px] text-amber-400 font-mono">⚠ Weight g/m not set on this Product Master item — kgs → meters conversion disabled</div>
                    ) : <div className="text-[10px] text-zinc-500">Pick an Item first to enable kgs → meters conversion</div>}
                  </div>
                );
              })()}
              <F label="Rejected (Kgs)">
                <FormattedNumberInput
                  step="0.001"
                  data-testid="entry-rejected-kgs"
                  value={form.rejected_kgs || 0}
                  onChange={e => {
                    const kgs = parseFloat(e.target.value) || 0;
                    const it = items.find(x => x.id === form.item_id);
                    const wgm = +(it?.weight_gm_per_meter || 0);
                    const meters = wgm > 0 ? Math.round(((kgs * 1000) / wgm) * 100) / 100 : form.rejected_meter;
                    setForm({ ...form, rejected_kgs: kgs, rejected_meter: wgm > 0 ? meters : form.rejected_meter });
                  }}
                  className={inp}
                  placeholder="0.000"
                />
              </F>
              <F label="Rejected (meters)">
                <FormattedNumberInput
                  data-testid="entry-rejected"
                  value={form.rejected_meter}
                  onChange={e => {
                    const meters = parseFloat(e.target.value) || 0;
                    const it = items.find(x => x.id === form.item_id);
                    const wgm = +(it?.weight_gm_per_meter || 0);
                    const kgs = wgm > 0 ? Math.round(((meters * wgm) / 1000) * 1000) / 1000 : form.rejected_kgs;
                    setForm({ ...form, rejected_meter: meters, rejected_kgs: wgm > 0 ? kgs : form.rejected_kgs });
                  }}
                  className={inp}
                />
              </F>
              {/* Feb 2026 · Reject Reason picker — fixed picklist so quality
                  can slice defects by cause (Colour off / Slub / Hole / Other). */}
              <F label="Reject Reason" className="col-span-2">
                <Select
                  value={form.rejected_reason || ""}
                  onValueChange={v => setForm({ ...form, rejected_reason: v })}
                >
                  <SelectTrigger data-testid="entry-reject-reason" className={selCls}>
                    <SelectValue placeholder={(form.rejected_meter > 0 || form.rejected_kgs > 0) ? "Pick a reason" : "— optional —"} />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                    {(defects.length > 0
                      ? defects.map(d => d.name).filter(Boolean)
                      : ["Colour off", "Slub", "Hole", "Other"]
                    ).map(r => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </F>
              <F label="Remarks" className="col-span-2"><input value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} className={inp} /></F>
            </div>
          )}

          {autoTransfer && tab !== "Packing" && !splitMode && (
            <div className="text-[11px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-sm px-3 py-2 flex items-center gap-2">
              <ArrowRightIcon size={12} weight="bold" />
              Saving will also create an <b>auto</b> Material Transfer of {tab === "Loom" ? loomProduction : (form.produced_meter || 0)} m to the next stage.
            </div>
          )}
          {!splitMode && (
          <DialogFooter>
            <button
              data-testid="clear-entry"
              onClick={() => {
                // Edit mode → restore the row exactly as it was on Edit
                // click (deep-cloned so subsequent tweaks stay isolated).
                // Create mode → wipe every field (existing behavior).
                if (editingId && originalSnapshot) {
                  setForm(JSON.parse(JSON.stringify(originalSnapshot)));
                  toast.success("Form reset to saved values");
                } else {
                  resetSingleEntry();
                }
              }}
              className="px-4 py-2 rounded-sm border border-red-500/40 text-sm text-red-300 hover:bg-red-500/10 flex items-center gap-1.5"
              title={editingId ? "Reset every field to the originally saved values" : "Wipe every field on this form"}
            >
              <TrashIcon size={12} weight="bold" /> {editingId ? "Clear" : "Clear All"}
            </button>
            <div className="flex-1" />
            <button onClick={() => { setOpen(false); setEditingId(""); setOriginalSnapshot(null); }} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400">Cancel</button>
            <button data-testid="save-entry" onClick={() => save(false)} className="px-4 py-2 rounded-sm bg-[var(--accent)] hover:bg-blue-600 text-sm font-semibold text-white">{editingId ? "Update" : `Save ${tab} Entry`}</button>
            {!editingId && tab === "Loom" && (
              <button
                data-testid="save-and-next"
                onClick={() => save(true)}
                className="px-4 py-2 rounded-sm bg-emerald-600 hover:bg-emerald-500 text-sm font-semibold text-white flex items-center gap-1.5"
                title="Save this entry and clear Machine + Hours + Qty for the next loom · Date stays"
              >
                Save & Next Machine <ArrowRightIcon size={12} weight="bold" />
              </button>
            )}
          </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Manual Material Transfer dialog */}
      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-2xl" description="Production Entry action — shift output, wastage and downtime capture.">
          <DialogHeader><DialogTitle className="font-display">New Material Transfer</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <F label="Date"><input type="date" value={txForm.transfer_date} onChange={e => setTxForm({ ...txForm, transfer_date: e.target.value })} className={inp} /></F>
            {/* Feb 2026 · dual-entry Qty: operators can key kgs (raw weight
                on the trolley) and the form auto-converts to meters using
                weight_gm_per_meter from Product Master. Formula:
                meters = (kgs × 1000) / weight_gm_per_meter. Both inputs
                stay editable so operators can override either side. */}
            <F label="Qty (Kgs)">
              <FormattedNumberInput
                step="0.001"
                data-testid="tx-qty-kgs"
                value={txForm.qty_kgs}
                onChange={e => {
                  const kgs = parseFloat(e.target.value) || 0;
                  const it = items.find(x => x.id === txForm.item_id);
                  const wgm = +(it?.weight_gm_per_meter || 0);
                  const meters = wgm > 0 ? (kgs * 1000) / wgm : txForm.qty_meters;
                  setTxForm({ ...txForm, qty_kgs: kgs, qty_meters: wgm > 0 ? Math.round(meters * 100) / 100 : txForm.qty_meters });
                }}
                className={inp}
                placeholder="0.000"
              />
            </F>
            <F label="Qty (Meters)">
              <FormattedNumberInput
                step="0.01"
                data-testid="tx-qty"
                value={txForm.qty_meters}
                onChange={e => setTxForm({ ...txForm, qty_meters: parseFloat(e.target.value) || 0 })}
                className={inp}
              />
            </F>
            {/* Conversion hint — reads weight_gm_per_meter live from the
                selected item so operators can sanity-check the maths. */}
            {(() => {
              const it = items.find(x => x.id === txForm.item_id);
              const wgm = +(it?.weight_gm_per_meter || 0);
              return (
                <div className="col-span-2 -mt-1">
                  {txForm.item_id ? (
                    wgm > 0 ? (
                      <div data-testid="tx-conversion-hint" className="text-[10px] text-zinc-500 font-mono">
                        Weight per meter: <span className="text-[var(--accent)]">{wgm} g/m</span> · Formula: meters = (kgs × 1000) ÷ {wgm}
                        {txForm.qty_kgs > 0 && <> · <span className="text-emerald-400">{(txForm.qty_kgs * 1000 / wgm).toFixed(2)} m from {txForm.qty_kgs} kg</span></>}
                      </div>
                    ) : <div className="text-[10px] text-amber-400 font-mono">⚠ Weight g/m not set on this Product Master item — kgs → meters conversion disabled</div>
                  ) : <div className="text-[10px] text-zinc-500">Pick an Item first to enable kgs → meters conversion</div>}
                </div>
              );
            })()}
            <F label="From Department">
              <Select value={txForm.from_stage} onValueChange={v => setTxForm({ ...txForm, from_stage: v })}>
                <SelectTrigger data-testid="tx-from" className={selCls}><SelectValue placeholder="Select department" /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                  {departments.length === 0 && <div className="px-3 py-2 text-xs text-zinc-500">No departments yet · add in Master Data</div>}
                  {departments.map(d => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="To Department">
              <Select value={txForm.to_stage} onValueChange={v => setTxForm({ ...txForm, to_stage: v })}>
                <SelectTrigger data-testid="tx-to" className={selCls}><SelectValue placeholder="Select department" /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                  {departments.length === 0 && <div className="px-3 py-2 text-xs text-zinc-500">No departments yet · add in Master Data</div>}
                  {departments.map(d => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Order">
              <Select value={txForm.order_id} onValueChange={v => setTxForm({ ...txForm, order_id: v })}>
                <SelectTrigger className={selCls}><SelectValue placeholder="Select order" /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                  {orders.map(o => <SelectItem key={o.id} value={o.id}>{o.order_no}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Item">
              <Select value={txForm.item_id} onValueChange={v => setTxForm({ ...txForm, item_id: v })}>
                <SelectTrigger className={selCls}><SelectValue placeholder="Select item" /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                  {items.map(i => <SelectItem key={i.id} value={i.id}>{i.code}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Remarks" className="col-span-2"><input value={txForm.remarks} onChange={e => setTxForm({ ...txForm, remarks: e.target.value })} className={inp} /></F>
          </div>
          <DialogFooter>
            <button onClick={() => setTransferOpen(false)} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400">Cancel</button>
            <button data-testid="save-transfer" onClick={saveTransfer} className="px-4 py-2 rounded-sm bg-[var(--accent)] hover:bg-blue-600 text-sm font-semibold text-white">Save Transfer</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
function F({ label, children, className = "" }) {
  return <div className={className}><div className="overline mb-1.5">{label}</div>{children}</div>;
}

// Read-only snapshot field used inside the Loom dialog for auto-populated
// master data (Model / Heads / Speed / Picks). Rendered on a distinct panel
// so operators immediately see they can't hand-edit these values.
function ReadonlyField({ label, value, testid }) {
  return (
    <div>
      <div className="overline mb-1.5 text-zinc-500">{label}</div>
      <div data-testid={testid} className="h-9 px-3 flex items-center bg-[var(--bg-1)] border border-[var(--border)] rounded-sm text-white font-mono text-sm">
        {value}
      </div>
    </div>
  );
}
